import { fetchInstance } from '@/utils/fetchInstance';
import { type OwnedPokemon, type Pokemon, type EvolutionData, type PendingMoveData } from '@/services/pokemon';

export interface MyPensionPokemon extends Omit<OwnedPokemon, 'pokemon'> {
  species: Pokemon;
}

export interface MyPension {
  pensionId: string;
  depositedAt: string;
  autoReleaseAt: string;
  elapsedHours: number;
  remainingHours: number;
  remainingMs: number;
  completed: boolean;
  estimatedRevenue: number;
  hourlyRevenue: number;
  estimatedHourlyRevenue: number;
  pokemon: MyPensionPokemon;
}

export interface PensionEntry {
  pensionId: string;
  owner: string;
  userId: string;
  depositedAt: string;
  autoReleaseAt: string;
  elapsedHours: number;
  remainingHours: number;
  completed: boolean;
  ownedPokemon: OwnedPokemon;
  pokemon: Pokemon;
}

export interface AllPensionsResponse {
  data: PensionEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface WithdrawPensionResponse {
  ownedPokemonId: string;
  pokeDollarsEarned: number;
  elapsedHours: number;
  completed: boolean;
  leveledUp: boolean;
  happinessGained: number;
  itemDropped: {
    itemId: number;
    itemType: string;
    name: string;
  } | null;
  cellDropped: boolean;
  retrievedLate: boolean;
  evolution?: EvolutionData;
  pendingMove?: PendingMoveData;
}

export interface UseSablierResponse {
  newAutoReleaseAt: string;
  sabliersConsumed: number;
}

export type AllPensionsSortBy = 'depositedAt' | 'level' | 'name' | 'rarity';
export type AllPensionsSortOrder = 'ASC' | 'DESC';

interface GetAllPensionsParams {
  offset?: number;
  limit?: number;
  sortBy?: AllPensionsSortBy;
  sortOrder?: AllPensionsSortOrder;
  search?: string;
}

export const getMyPension = (): Promise<MyPension | null> =>
  fetchInstance.get<MyPension | null>('pension');

export const getAllPensions = ({
  offset = 0,
  limit = 25,
  sortBy = 'depositedAt',
  sortOrder = 'DESC',
  search,
}: GetAllPensionsParams = {}): Promise<AllPensionsResponse> =>
  fetchInstance.get<AllPensionsResponse>('pension/all', {
    params: {
      offset,
      limit,
      sortBy,
      sortOrder,
      ...(search ? { search } : {}),
    },
  });

export const depositPokemon = (ownedPokemonId: string): Promise<void> =>
  fetchInstance.post<void>('pension', { ownedPokemonId });

export const withdrawPokemon = (): Promise<WithdrawPensionResponse> =>
  fetchInstance.delete<WithdrawPensionResponse>('pension');

export const useSablier = (): Promise<UseSablierResponse> =>
  fetchInstance.post<UseSablierResponse>('pension/sablier', { hoursToReduce: 1 });
